import { SortConfig } from '../models';
import { CSSProperties, KeyboardEvent } from 'react';

export const setCookie = (name: string, value: string) => {
  let cookieString = encodeURIComponent(name) + '=' + encodeURIComponent(value);
  document.cookie = cookieString;
};

export const getCookie = (name: string) => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [cookieName, cookieValue] = cookie.split('=').map((c) => c.trim());
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
};

export const eventstopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
};

export const eventStop = (e: React.MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

export const getWebsocketUrl = () => {
  const sub_domain = location.hostname.split('.');
  const ns =
    sub_domain.length === 2 || sub_domain[0] === 'www' ? 'dev' : sub_domain[0];
  let socketsURL;
  if (sub_domain.length === 3 && sub_domain[0] === 'www') {
    socketsURL = `${location.hostname}/${ns}`;
  } else if (
    ns !== 'test' &&
    ns !== 'demo' &&
    ns !== 'data' &&
    ns !== 'dev' &&
    ns !== 'local' &&
    ns !== 'staging'
  ) {
    socketsURL = `${location.protocol}//${location.hostname}/dev`;
  } else {
    // Use protocol without explicit port - let browser use default ports (443 for https, 80 for http)
    socketsURL = `${location.protocol}//${location.hostname}/${ns}`;
  }
  return socketsURL;
};

export const toDecimal = (
  integer: number | string,
  count = 2,
  round = false,
) => {
  let num = +integer || 0;
  if (round) num = Math.round(num);
  return num.toFixed(count);
};

export const arrayToObject = (list: any[]) => {
  const obj: any = {};
  list.map((el) => (obj[el.id] = el));
  return obj;
};

export const getQueryString = (
  obj: { [key: string]: any },
  onlyExisted?: boolean,
) => {
  if (!obj) return '';
  let queryString = '';
  Object.entries(obj).map((el, idx) => {
    if (onlyExisted && !el[1]) queryString += `${idx ? '' : '?'}`;
    else queryString += `${idx ? '&' : '?'}${el[0]}=${el[1]}`;
  });
  return queryString;
};

export const formattedNumber = (
  value: number | string | null,
  decimal = 0,
  numberSeparator = false,
  type = 'text',
) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return type === 'number' ? 0 : '';
  }

  // Convert string numbers to numbers for proper formatting
  const numValue =
    typeof value === 'string' && value !== '' ? Number(value) : value;

  // If not a valid number, return empty string or 0
  if (typeof numValue !== 'number' || isNaN(numValue)) {
    return type === 'number' ? 0 : '';
  }

  // If type is not 'number', return the value as-is (or empty string)
  if (type !== 'number') {
    return value || '';
  }

  // Format the number based on decimal and separator settings
  if (decimal && !numberSeparator) {
    return Number(numValue).toFixed(2);
  } else if (!decimal && numberSeparator) {
    return numValue.toLocaleString();
  } else if (decimal && numberSeparator) {
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
  } else {
    return numValue;
  }
};

export const formattedString = (value: string | number, decimal = 0) => {
  return (decimal ? parseFloat : parseInt)(String(value).replace(/,/g, ''));
};

export const getSectionQueryString = (
  params: { [key: string]: any },
  extra_params?: { [key: string]: any },
) => {
  let str = '';
  const data = extra_params ? { ...params, ...extra_params } : params;
  Object.entries(data).forEach((el) => {
    if (el[1] || typeof el[1] === 'boolean')
      str += `${str ? '&' : ''}${el[0]}=${el[1]}`;
  });
  return str;
};

export const getPositionType = (id?: number, hood?: boolean) => {
  if (!id) return '';
  else if (id < 7 || id === 9 || hood) return 'on_cabinet';
  else if (id > 7) return 'on_internal_walls';
};

export const createNewName = (init = 'AH-') => {
  const d = new Date();
  const year = d.getFullYear().toString().substr(-2);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  const checkLength = (val: number) =>
    String(val).length === 1 ? `0${val}` : val;

  return `${init}${year}${checkLength(month)}${checkLength(day)}_${checkLength(
    hours,
  )}${checkLength(minutes)}${checkLength(seconds)}`;
};

export const handleSortObject = (items: any[], newSortConfig: SortConfig) => {
  const { accessor, direction } = newSortConfig;
  return [...items].sort((a, b) => {
    const aVal = a?.[accessor] ?? '';
    const bVal = b?.[accessor] ?? '';

    if (aVal === bVal) return 0;
    if (aVal === undefined || bVal === undefined) return 0;

    const comparison = (() => {
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal - bVal;
      }
      return 0;
    })();

    return direction === 'asc' ? comparison : -comparison;
  });
};

export const generateTimestamp = () => {
  const now = new Date();

  const pad = (num: number) => String(num).padStart(2, '0');

  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const seconds = pad(now.getSeconds());

  return `${year}.${month}.${day}_${hours}:${minutes}:${seconds}`;
};

export const acceptOnlyEnglish = (e: KeyboardEvent) => {
  // Allow control/navigation keys
  if (e.ctrlKey || e.metaKey || e.altKey || e.key.length > 1) {
    return;
  }

  // Block non-ASCII characters
  const isASCII = /^[\x00-\x7F]$/.test(e.key);
  if (!isASCII) {
    e.preventDefault();
  }
};

export const converToNumber = (s: any) => {
  return isNaN(s) || s === null || s === '' || s === undefined ? null : +s;
};

export const getObjectChanges = (obj1: any, obj2: any, escape: any) => {
  const changes: any = {};
  for (const key in obj2) {
    if (
      typeof obj2[key] === 'object' &&
      obj2[key] !== null &&
      !Array.isArray(obj2[key])
    ) {
      const nested = getObjectChanges(obj1[key] || {}, obj2[key], escape || {});
      if (Object.keys(nested).length > 0 && !escape[key]) {
        changes[key] = nested;
      }
    } else if (obj2[key] !== obj1[key] && !escape[key]) {
      changes[key] = obj2[key];
    }
  }

  return changes;
};

export const generateTimestampedName = (prefix: string = 'AH-'): string => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const padZero = (value: number): string => value.toString().padStart(2, '0');

  return `${prefix}${year}${padZero(month)}${padZero(day)}_${padZero(
    hours,
  )}${padZero(minutes)}${padZero(seconds)}`;
};

export const restrictToYAxis = (style?: CSSProperties) => {
  if (!style || !style.transform) return style;

  const translate3dMatch =
    /translate3d\(([-\d.]+)px,\s*([-\d.]+)px,\s*([-\d.]+)px\)/.exec(
      style.transform,
    );
  if (translate3dMatch) {
    const [, , y, z] = translate3dMatch;
    return { ...style, transform: `translate3d(0px, ${y}px, ${z}px)` };
  }

  const translateMatch = /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/.exec(
    style.transform,
  );
  if (translateMatch) {
    const [, , y] = translateMatch;
    return { ...style, transform: `translate(0px, ${y}px)` };
  }

  return style;
};

export const restrictToXAxis = (style?: CSSProperties) => {
  if (!style || !style.transform) return style;

  const translate3dMatch =
    /translate3d\(([-\d.]+)px,\s*([-\d.]+)px,\s*([-\d.]+)px\)/.exec(
      style.transform,
    );
  if (translate3dMatch) {
    const [, x, , z] = translate3dMatch;
    return { ...style, transform: `translate3d(${x}px, 0px, ${z}px)` };
  }

  const translateMatch = /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/.exec(
    style.transform,
  );
  if (translateMatch) {
    const [, x] = translateMatch;
    return { ...style, transform: `translate(${x}px, 0px)` };
  }

  return style;
};

export const restrictToAxis = (style?: CSSProperties, axis?: 'x' | 'y') => {
  if (!style || !style.transform || !axis) return style;
  const f = {
    y: restrictToYAxis,
    x: restrictToXAxis,
  };
  return f[axis]();
};

export const parseValue = (value: any) => {
  if (value === null || value === '') return null;
  const n = Number(value);
  return !Number.isNaN(n) ? n : value;
};

export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));
